import { AuthenticationRequiredError, CloudApiError } from './api-client'

/**
 * 備份失敗的分類。
 *
 * 「批次動作遇錯即停」的理由是：批次失敗的常見成因是系統性的，逐本重試只會把同一個
 * 錯誤重複 N 次，而使用者要看的是一次原因。這個理由對系統性錯誤成立，對單本錯誤不成立
 * ——某一本的資料夾被改名，不影響其他十九本。因此依錯誤的性質分流，而不是一律停。
 *
 * 分類一律以錯誤的型別與狀態碼判定，不比對錯誤訊息字串：訊息是給人看的，改一次措辭
 * 就會讓分類靜默失準。
 */
export type BackupFailureKind = 'none' | 'item' | 'systemic'

/** 來源資料夾尚未授權。獨立型別，讓分類不必猜訊息內容。 */
export class SourceUnauthorizedError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'SourceUnauthorizedError'
  }
}

/** 瀏覽器在權限與配額上用 DOMException 表達的系統性狀況。 */
const systemicDomExceptions = ['QuotaExceededError', 'NotAllowedError', 'SecurityError']

/** 包裝層數上限，避免自我參照的 cause 造成無限迴圈。 */
const MAX_CAUSE_DEPTH = 5

export function backupFailureKind(cause: unknown, depth = 0): Exclude<BackupFailureKind, 'none'> {
  // 登入失效、伺服器端暫時故障、儲存空間不足、來源權限被撤銷：
  // 這四種在下一本身上會以一模一樣的形式再發生一次。
  if (cause instanceof SourceUnauthorizedError) return 'systemic'
  if (cause instanceof AuthenticationRequiredError) return 'systemic'
  if (cause instanceof CloudApiError && cause.status >= 500) return 'systemic'
  if (cause instanceof DOMException && systemicDomExceptions.includes(cause.name)) return 'systemic'
  // 中途被包成訊息更完整的 Error 時，真正的型別在 cause 裡。
  if (depth < MAX_CAUSE_DEPTH && cause instanceof Error && cause.cause !== undefined) {
    return backupFailureKind(cause.cause, depth + 1)
  }
  return 'item'
}
